 
import React, { useState } from 'react';
import { ShoppingCart, Heart, Menu, X, Plus, Minus, Trash2, Mail, Phone, MapPin } from 'lucide-react';

// Products data - you can easily modify these
const PRODUCTS = [
  {
    id: 1,
    name: "Birthday Celebration Balloon Bouquet",
    category: "birthday",
    type: "handmade",
    basePrice: 45.00,
    image: "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=500",
    description: "Beautiful handmade balloon bouquet perfect for birthdays",
    colorOptions: ["Gold", "Silver", "Rose Gold", "Rainbow"],
    styleOptions: ["Classic", "Deluxe", "Premium"]
  },
  {
    id: 2,
    name: "Anniversary Love Balloons",
    category: "anniversary",
    type: "handmade",
    basePrice: 55.00,
    image: "https://images.unsplash.com/photo-1464349095431-e9a21285b5f3?w=500",
    description: "Romantic balloon arrangement for anniversaries",
    colorOptions: ["Red", "Pink", "White", "Red & White"],
    styleOptions: ["Romantic", "Elegant", "Grand"]
  },
  {
    id: 3,
    name: "Graduation Success Balloons",
    category: "graduation",
    type: "pod",
    basePrice: 38.00,
    image: "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=500",
    description: "Celebrate their achievement with custom graduation balloons",
    colorOptions: ["Black & Gold", "School Colors", "Silver & Blue"],
    styleOptions: ["Standard", "Deluxe"]
  },
  {
    id: 4,
    name: "New Baby Celebration",
    category: "baby",
    type: "handmade",
    basePrice: 48.00,
    image: "https://images.unsplash.com/photo-1515488042361-ee00e0ddd4e4?w=500",
    description: "Welcome the new arrival with adorable baby balloons",
    colorOptions: ["Blue", "Pink", "Yellow", "Mint Green"],
    styleOptions: ["Sweet", "Deluxe", "Premium"]
  },
  {
    id: 5,
    name: "Congratulations Balloon Set",
    category: "congratulations",
    type: "pod",
    basePrice: 35.00,
    image: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=500",
    description: "Perfect for any congratulatory occasion",
    colorOptions: ["Gold", "Multi-color", "Elegant Mix"],
    styleOptions: ["Standard", "Premium"]
  }
];

const OCCASIONS = [
  { id: "birthday", name: "Birthday", emoji: "🎂" },
  { id: "anniversary", name: "Anniversary", emoji: "💕" },
  { id: "graduation", name: "Graduation", emoji: "🎓" },
  { id: "congratulations", name: "Congratulations", emoji: "🎉" },
  { id: "baby", name: "New Baby", emoji: "👶" },
  { id: "valentines", name: "Valentine's", emoji: "💝" },
  { id: "mothers", name: "Mother's Day", emoji: "🌸" },
  { id: "fathers", name: "Father's Day", emoji: "👔" }
];

export default function GiftsByKarlene() {
  const [currentView, setCurrentView] = useState('home');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Customization state
  const [customText, setCustomText] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedStyle, setSelectedStyle] = useState('');
  
  // Checkout form state
  const [checkoutForm, setCheckoutForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    notes: ''
  });

  const filteredProducts = selectedCategory 
    ? PRODUCTS.filter(p => p.category === selectedCategory)
    : PRODUCTS;

  const addToCart = (product, customization) => {
    const cartItem = {
      ...product,
      cartId: Date.now(),
      customization,
      quantity: 1
    };
    setCart([...cart, cartItem]);
    setSelectedProduct(null);
    setCustomText('');
    setSelectedColor('');
    setSelectedStyle('');
  };

  const removeFromCart = (cartId) => {
    setCart(cart.filter(item => item.cartId !== cartId));
  };

  const updateQuantity = (cartId, change) => {
    setCart(cart.map(item => 
      item.cartId === cartId 
        ? {...item, quantity: Math.max(1, item.quantity + change)}
        : item
    ));
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.basePrice * item.quantity), 0);
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const handleCheckout = () => {
    // Create order summary
    const orderSummary = {
      orderNumber: 'GBK-' + Date.now(),
      date: new Date().toLocaleString(),
      customer: checkoutForm,
      items: cart,
      total: cartTotal.toFixed(2)
    };

    // Create email body
    const emailBody = `
NEW ORDER - GIFTS BY KARLENE

Order #: ${orderSummary.orderNumber}
Date: ${orderSummary.date}

CUSTOMER INFO:
Name: ${checkoutForm.name}
Email: ${checkoutForm.email}
Phone: ${checkoutForm.phone}

SHIPPING ADDRESS:
${checkoutForm.address}
${checkoutForm.city}, ${checkoutForm.state} ${checkoutForm.zip}

SPECIAL NOTES:
${checkoutForm.notes || 'None'}

ORDER ITEMS:
${cart.map(item => `
- ${item.name} (x${item.quantity}) - $${(item.basePrice * item.quantity).toFixed(2)}
  Color: ${item.customization.color}
  Style: ${item.customization.style}
  Custom Text: "${item.customization.text || 'None'}"
`).join('\n')}

TOTAL: $${cartTotal.toFixed(2)}

---
Please respond to customer at: ${checkoutForm.email}
    `.trim();

    // Send order via mailto (opens email client)
    const subject = `New Order ${orderSummary.orderNumber} - Gifts By Karlene`;
    const mailtoLink = `mailto:Karlenecwallace@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(emailBody)}`;
    
    // Also prepare WhatsApp message as backup
    const whatsappMessage = `🎈 *NEW ORDER*\n\nOrder: ${orderSummary.orderNumber}\nCustomer: ${checkoutForm.name}\nEmail: ${checkoutForm.email}\nPhone: ${checkoutForm.phone}\n\nItems: ${cart.length}\nTotal: $${cartTotal.toFixed(2)}\n\nFull details sent via email!`;
    const whatsappLink = `https://wa.me/17188839050?text=${encodeURIComponent(whatsappMessage)}`;

    // Show confirmation with options
    const sendViaEmail = window.confirm(
      `Order ready to send!\n\nClick OK to send via Email\nClick Cancel to send via WhatsApp instead`
    );

    if (sendViaEmail) {
      window.open(mailtoLink, '_blank');
    } else {
      window.open(whatsappLink, '_blank');
    }

    // Clear cart and show success
    alert(`✅ Order ${orderSummary.orderNumber} created!\n\nWe'll contact you at ${checkoutForm.email} to arrange payment and delivery.`);
    setCart([]);
    setShowCheckout(false);
    setShowCart(false);
    setCheckoutForm({
      name: '', email: '', phone: '', address: '', city: '', state: '', zip: '', notes: ''
    });
  };

  // Home View
  const HomeView = () => (
    <div className="space-y-12">
      {/* Hero Section */}
      <div className="relative h-96 rounded-2xl overflow-hidden">
        <img 
          src="https://images.unsplash.com/photo-1464349095431-e9a21285b5f3?w=1200"
          alt="Balloon gifts"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/80 to-pink-900/80 flex items-center justify-center">
          <div className="text-center text-white px-4">
            <h1 className="text-5xl font-bold mb-4">GIFTS BY KARLENE</h1>
            <p className="text-xl mb-8">Personalized Balloon Gifts for Every Occasion</p>
            <button 
              onClick={() => setCurrentView('shop')}
              className="bg-white text-purple-900 px-8 py-3 rounded-full font-semibold hover:bg-purple-100 transition"
            >
              Shop Now
            </button>
          </div>
        </div>
      </div>

      {/* Occasions Grid */}
      <div>
        <h2 className="text-3xl font-bold text-center mb-8">Shop by Occasion</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {OCCASIONS.map(occasion => (
            <button
              key={occasion.id}
              onClick={() => {
                setSelectedCategory(occasion.id);
                setCurrentView('shop');
              }}
              className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl hover:shadow-lg transition text-center"
            >
              <div className="text-4xl mb-2">{occasion.emoji}</div>
              <div className="font-semibold text-gray-800">{occasion.name}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Featured Products */}
      <div>
        <h2 className="text-3xl font-bold text-center mb-8">Featured Gifts</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {PRODUCTS.slice(0, 3).map(product => (
            <div key={product.id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition">
              <img src={product.image} alt={product.name} className="w-full h-48 object-cover"/>
              <div className="p-4">
                <h3 className="font-semibold text-lg mb-2">{product.name}</h3>
                <p className="text-gray-600 text-sm mb-3">{product.description}</p>
                <div className="flex justify-between items-center">
                  <span className="text-2xl font-bold text-purple-600">${product.basePrice}</span>
                  <button 
                    onClick={() => setSelectedProduct(product)}
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition"
                  >
                    Customize
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* How It Works */}
      <div className="bg-white rounded-2xl p-8 shadow-lg">
        <h2 className="text-3xl font-bold text-center mb-8">How It Works</h2>
        <div className="grid md:grid-cols-3 gap-6 text-center">
          <div>
            <div className="text-5xl mb-4">1️⃣</div>
            <h3 className="font-bold text-lg mb-2">Choose Your Gift</h3>
            <p className="text-gray-600">Browse our collection and select the perfect balloon gift</p>
          </div>
          <div>
            <div className="text-5xl mb-4">2️⃣</div>
            <h3 className="font-bold text-lg mb-2">Customize It</h3>
            <p className="text-gray-600">Add personal text, choose colors and style</p>
          </div>
          <div>
            <div className="text-5xl mb-4">3️⃣</div>
            <h3 className="font-bold text-lg mb-2">Place Order</h3>
            <p className="text-gray-600">Submit your order and we'll contact you for payment & delivery</p>
          </div>
        </div>
      </div>
    </div>
  );

  // Shop View
  const ShopView = () => (
    <div>
      <div className="mb-8">
        <h2 className="text-3xl font-bold mb-4">
          {selectedCategory 
            ? OCCASIONS.find(o => o.id === selectedCategory)?.name + ' Gifts'
            : 'All Products'}
        </h2>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-4 py-2 rounded-full transition ${!selectedCategory ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          >
            All
          </button>
          {OCCASIONS.map(occasion => (
            <button
              key={occasion.id}
              onClick={() => setSelectedCategory(occasion.id)}
              className={`px-4 py-2 rounded-full transition ${selectedCategory === occasion.id ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              {occasion.emoji} {occasion.name}
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {filteredProducts.map(product => (
          <div key={product.id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition">
            <div className="relative">
              <img src={product.image} alt={product.name} className="w-full h-48 object-cover"/>
              <span className="absolute top-2 right-2 bg-purple-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                {product.type === 'handmade' ? '✋ Handmade' : '🖨️ Print on Demand'}
              </span>
            </div>
            <div className="p-4">
              <h3 className="font-semibold text-lg mb-2">{product.name}</h3>
              <p className="text-gray-600 text-sm mb-3">{product.description}</p>
              <div className="flex justify-between items-center">
                <span className="text-2xl font-bold text-purple-600">${product.basePrice}</span>
                <button 
                  onClick={() => {
                    setSelectedProduct(product);
                    setSelectedColor(product.colorOptions[0]);
                    setSelectedStyle(product.styleOptions[0]);
                  }}
                  className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition"
                >
                  Customize
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Customization Modal
  const CustomizationModal = () => {
    if (!selectedProduct) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-2xl font-bold">{selectedProduct.name}</h2>
              <button onClick={() => setSelectedProduct(null)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>

            <img src={selectedProduct.image} alt={selectedProduct.name} className="w-full h-64 object-cover rounded-lg mb-6"/>

            <div className="space-y-6">
              {/* Custom Text */}
              <div>
                <label className="block font-semibold mb-2">Custom Text (Optional)</label>
                <input
                  type="text"
                  value={customText}
                  onChange={(e) => setCustomText(e.target.value)}
                  placeholder="e.g., Happy Birthday Sarah!"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                  maxLength={50}
                />
                <p className="text-sm text-gray-500 mt-1">{customText.length}/50 characters</p>
              </div>

              {/* Color Selection */}
              <div>
                <label className="block font-semibold mb-2">Choose Color</label>
                <div className="grid grid-cols-2 gap-2">
                  {selectedProduct.colorOptions.map(color => (
                    <button
                      key={color}
                      onClick={() => setSelectedColor(color)}
                      className={`p-3 rounded-lg border-2 transition ${selectedColor === color ? 'border-purple-600 bg-purple-50' : 'border-gray-300 hover:border-purple-300'}`}
                    >
                      {color}
                    </button>
                  ))}
                </div>
              </div>

              {/* Style Selection */}
              <div>
                <label className="block font-semibold mb-2">Choose Style</label>
                <div className="grid grid-cols-2 gap-2">
                  {selectedProduct.styleOptions.map(style => (
                    <button
                      key={style}
                      onClick={() => setSelectedStyle(style)}
                      className={`p-3 rounded-lg border-2 transition ${selectedStyle === style ? 'border-purple-600 bg-purple-50' : 'border-gray-300 hover:border-purple-300'}`}
                    >
                      {style}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price */}
              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-semibold">Total Price:</span>
                  <span className="text-3xl font-bold text-purple-600">${selectedProduct.basePrice}</span>
                </div>
              </div>

              {/* Add to Cart Button */}
              <button
                onClick={() => {
                  if (selectedColor && selectedStyle) {
                    addToCart(selectedProduct, {
                      text: customText,
                      color: selectedColor,
                      style: selectedStyle
                    });
                  }
                }}
                disabled={!selectedColor || !selectedStyle}
                className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Add to Cart
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Cart Sidebar
  const CartSidebar = () => (
    <div className={`fixed right-0 top-0 h-full w-full md:w-96 bg-white shadow-2xl transform transition-transform z-50 ${showCart ? 'translate-x-0' : 'translate-x-full'}`}>
      <div className="p-6 h-full flex flex-col">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Your Cart ({cartItemCount})</h2>
          <button onClick={() => setShowCart(false)} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        {cart.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <ShoppingCart size={64} className="mx-auto mb-4 opacity-20" />
              <p>Your cart is empty</p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto space-y-4 mb-6">
              {cart.map(item => (
                <div key={item.cartId} className="border rounded-lg p-4">
                  <div className="flex gap-3 mb-3">
                    <img src={item.image} alt={item.name} className="w-20 h-20 object-cover rounded"/>
                    <div className="flex-1">
                      <h3 className="font-semibold text-sm">{item.name}</h3>
                      <p className="text-xs text-gray-600">{item.customization.color} • {item.customization.style}</p>
                      {item.customization.text && (
                        <p className="text-xs text-purple-600 mt-1">"{item.customization.text}"</p>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <button onClick={() => updateQuantity(item.cartId, -1)} className="p-1 hover:bg-gray-100 rounded">
                        <Minus size={16} />
                      </button>
                      <span className="w-8 text-center">{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.cartId, 1)} className="p-1 hover:bg-gray-100 rounded">
                        <Plus size={16} />
                      </button>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-bold">${(item.basePrice * item.quantity).toFixed(2)}</span>
                      <button onClick={() => removeFromCart(item.cartId)} className="text-red-500 hover:text-red-700">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t pt-4 space-y-4">
              <div className="flex justify-between text-xl font-bold">
                <span>Total:</span>
                <span className="text-purple-600">${cartTotal.toFixed(2)}</span>
              </div>
              <button 
                onClick={() => {
                  setShowCart(false);
                  setShowCheckout(true);
                }}
                className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 transition"
              >
                Proceed to Checkout
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  // Checkout Modal
  const CheckoutModal = () => {
    if (!showCheckout) return null;

    const isFormValid = checkoutForm.name && checkoutForm.email && checkoutForm.phone && 
                        checkoutForm.address && checkoutForm.city && checkoutForm.state && checkoutForm.zip;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
        <div className="bg-white rounded-2xl max-w-2xl w-full my-8">
          <div className="p-6">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-bold">Complete Your Order</h2>
              <button onClick={() => setShowCheckout(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block font-semibold mb-2">Full Name *</label>
                  <input
                    type="text"
                    value={checkoutForm.name}
                    onChange={(e) => setCheckoutForm({...checkoutForm, name: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-600"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block font-semibold mb-2">Email *</label>
                  <input
                    type="email"
                    value={checkoutForm.email}
                    onChange={(e) => setCheckoutForm({...checkoutForm, email: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-600"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div>
                <label className="block font-semibold mb-2">Phone Number *</label>
                <input
                  type="tel"
                  value={checkoutForm.phone}
                  onChange={(e) => setCheckoutForm({...checkoutForm, phone: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-600"
                  placeholder="(123) 456-7890"
                />
              </div>

              <div>
                <label className="block font-semibold mb-2">Delivery Address *</label>
                <input
                  type="text"
                  value={checkoutForm.address}
                  onChange={(e) => setCheckoutForm({...checkoutForm, address: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-600"
                  placeholder="123 Main Street"
                />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block font-semibold mb-2">City *</label>
                  <input
                    type="text"
                    value={checkoutForm.city}
                    onChange={(e) => setCheckoutForm({...checkoutForm, city: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-600"
                    placeholder="New York"
                  />
                </div>
                <div>
                  <label className="block font-semibold mb-2">State *</label>
                  <input
                    type="text"
                    value={checkoutForm.state}
                    onChange={(e) => setCheckoutForm({...checkoutForm, state: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-600"
                    placeholder="NY"
                    maxLength={2}
                  />
                </div>
                <div>
                  <label className="block font-semibold mb-2">ZIP Code *</label>
                  <input
                    type="text"
                    value={checkoutForm.zip}
                    onChange={(e) => setCheckoutForm({...checkoutForm, zip: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-600"
                    placeholder="10001"
                  />
                </div>
              </div>

              <div>
                <label className="block font-semibold mb-2">Special Instructions (Optional)</label>
                <textarea
                  value={checkoutForm.notes}
                  onChange={(e) => setCheckoutForm({...checkoutForm, notes: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-600"
                  rows={3}
                  placeholder="Any special delivery instructions or notes..."
                />
              </div>
            </div>

            <div className="bg-purple-50 p-4 rounded-lg mb-6">
              <h3 className="font-bold mb-2">Order Summary</h3>
              <div className="text-sm space-y-1 mb-3">
                {cart.map(item => (
                  <div key={item.cartId} className="flex justify-between">
                    <span>{item.name} (x{item.quantity})</span>
                    <span>${(item.basePrice * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between text-xl font-bold border-t pt-2">
                <span>Total:</span>
                <span className="text-purple-600">${cartTotal.toFixed(2)}</span>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                💳 <strong>Payment:</strong> We'll contact you via email or phone to arrange payment (Cash, Venmo, Zelle, etc.) and confirm delivery details.
              </p>
            </div>

            <button
              onClick={handleCheckout}
              disabled={!isFormValid}
              className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Place Order
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <button onClick={() => setCurrentView('home')} className="flex items-center gap-2">
              <div className="text-3xl">🎈</div>
              <div>
                <div className="text-xl font-bold text-purple-600">GIFTS BY KARLENE</div>
                <div className="text-xs text-gray-500">Personalized Balloon Gifts</div>
              </div>
            </button>

            <nav className="hidden md:flex gap-6 items-center">
              <button onClick={() => setCurrentView('home')} className="hover:text-purple-600 transition">Home</button>
              <button onClick={() => { setCurrentView('shop'); setSelectedCategory(null); }} className="hover:text-purple-600 transition">Shop</button>
              <button onClick={() => setCurrentView('contact')} className="hover:text-purple-600 transition">Contact</button>
            </nav>

            <div className="flex items-center gap-4">
              <button 
                onClick={() => setShowCart(true)}
                className="relative hover:text-purple-600 transition"
              >
                <ShoppingCart size={24} />
                {cartItemCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-purple-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {cartItemCount}
                  </span>
                )}
              </button>
              <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                <Menu size={24} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {currentView === 'home' && <HomeView />}
        {currentView === 'shop' && <ShopView />}
        {currentView === 'contact' && (
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Contact Us</h2>
            <div className="bg-white rounded-2xl p-8 shadow-lg space-y-6">
              <div className="flex items-start gap-4">
                <Mail className="text-purple-600 mt-1" size={24} />
                <div>
                  <h3 className="font-bold mb-1">Email</h3>
                  <p className="text-gray-600">Karlenecwallace@gmail.com</p>
                  <p className="text-sm text-gray-500 mt-1">We respond within 24 hours</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone className="text-purple-600 mt-1" size={24} />
                <div>
                  <h3 className="font-bold mb-1">Phone / WhatsApp</h3>
                  <p className="text-gray-600">(718) 883-9050</p>
                  <p className="text-sm text-gray-500 mt-1">Mon-Sat, 9am-6pm</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MapPin className="text-purple-600 mt-1" size={24} />
                <div>
                  <h3 className="font-bold mb-1">Service Area</h3>
                  <p className="text-gray-600">Local delivery available</p>
                  <p className="text-sm text-gray-500 mt-1">Nationwide shipping for select items</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white mt-16">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="font-bold text-lg mb-4">GIFTS BY KARLENE</h3>
              <p className="text-gray-400 text-sm">Creating personalized balloon gifts for every special moment in your life.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Shop</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><button onClick={() => { setSelectedCategory('birthday'); setCurrentView('shop'); }}>Birthday</button></li>
                <li><button onClick={() => { setSelectedCategory('anniversary'); setCurrentView('shop'); }}>Anniversary</button></li>
                <li><button onClick={() => { setSelectedCategory('graduation'); setCurrentView('shop'); }}>Graduation</button></li>
                <li><button onClick={() => { setSelectedCategory('baby'); setCurrentView('shop'); }}>New Baby</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Info</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><button onClick={() => setCurrentView('contact')}>Contact Us</button></li>
                <li>Delivery Info</li>
                <li>Custom Orders</li>
                <li>FAQ</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Connect</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>Instagram</li>
                <li>Facebook</li>
                <li>Pinterest</li>
                <li>TikTok</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-400">
            © 2026 Gifts By Karlene. All rights reserved.
          </div>
        </div>
      </footer>

      {/* Modals */}
      {selectedProduct && <CustomizationModal />}
      <CartSidebar />
      <CheckoutModal />
    </div>
  );
}